import { locationManager } from "@telegram-apps/sdk-react";
import { isTelegramWebApp } from "@/app/telegram";
import { CITIES, CITY_ORDER, CityKey } from "@/features/services";

export interface GeoResult {
  lat: number;
  lon: number;
  city: CityKey;
  /** До центра ближайшего города, км. */
  distanceKm: number;
}

export class GeoError extends Error {
  constructor(
    message: string,
    public readonly reason: "denied" | "unavailable" | "timeout"
  ) {
    super(message);
  }
}

const R = 6371;
const rad = (d: number) => (d * Math.PI) / 180;

export function distanceKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = rad(bLat - aLat);
  const dLon = rad(bLon - aLon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function nearestCityTo(lat: number, lon: number): { city: CityKey; distanceKm: number } {
  let best: CityKey = CITY_ORDER[0];
  let bestD = Infinity;
  CITY_ORDER.forEach((key) => {
    const c = CITIES[key].center;
    const d = distanceKm(lat, lon, c.lat, c.lon);
    if (d < bestD) {
      bestD = d;
      best = key;
    }
  });
  return { city: best, distanceKm: bestD };
}

const withTimeout = <T,>(p: Promise<T>, ms: number, reason: GeoError["reason"]) =>
  new Promise<T>((resolve, reject) => {
    const id = window.setTimeout(
      () => reject(new GeoError("Не удалось определить позицию — попробуйте ещё раз", reason)),
      ms
    );
    p.then(
      (v) => {
        window.clearTimeout(id);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(id);
        reject(e);
      }
    );
  });

/**
 * Telegram отдаёт координаты через свой менеджер, а не через браузерный API.
 * Только в настоящем Mini App: в mock-окружении браузера `isSupported()` тоже
 * отвечает «да», но на запросы менеджера никто не отвечает — `mount()` висит
 * вечно, и до браузерной геолокации дело не доходит.
 */
async function viaTelegram(): Promise<{ lat: number; lon: number } | null> {
  if (!isTelegramWebApp()) return null;
  try {
    if (!locationManager.isSupported()) return null;
    if (!locationManager.isMounted()) await withTimeout(locationManager.mount(), 5000, "timeout");
    if (!locationManager.isAccessGranted()) {
      // Пользователь запретил в настройках Telegram — браузерный API тут не поможет.
      throw new GeoError("Доступ к геопозиции запрещён в Telegram", "denied");
    }
    const loc = await withTimeout(locationManager.requestLocation(), 15000, "timeout");
    return { lat: loc.latitude, lon: loc.longitude };
  } catch (e) {
    if (e instanceof GeoError && e.reason === "denied") throw e;
    // Менеджер не ответил или упал — пробуем браузерный API, он в webview тоже есть.
    return null;
  }
}

function viaBrowser(): Promise<{ lat: number; lon: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new GeoError("Геопозиция недоступна в этом браузере", "unavailable"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          reject(new GeoError("Доступ к геопозиции запрещён", "denied"));
        } else if (err.code === err.TIMEOUT) {
          reject(new GeoError("Не удалось определить позицию — попробуйте ещё раз", "timeout"));
        } else {
          reject(new GeoError("Геопозиция сейчас недоступна", "unavailable"));
        }
      },
      { timeout: 10000, maximumAge: 300e3, enableHighAccuracy: false }
    );
  });
}

/** Координаты → ближайший из городов демо. Бросает GeoError с человеческим текстом. */
export async function detectCity(): Promise<GeoResult> {
  // Верхняя граница на всё вместе: пока браузер показывает запрос разрешения,
  // его собственный timeout не тикает, и без этого колесо крутилось бы вечно.
  const point = await withTimeout(
    (async () => (await viaTelegram()) ?? (await viaBrowser()))(),
    30000,
    "timeout"
  );
  return { ...point, ...nearestCityTo(point.lat, point.lon) };
}

/** Короткая подпись расстояния для шторки: «в городе» / «120 км». */
export function distanceLabel(km: number): string {
  if (km < 40) return "вы в городе";
  return `${Math.round(km)} км от центра`;
}
